import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heart, Users, Brain, ArrowRight, Clock, MapPin, ChevronDown, Briefcase } from 'lucide-react';

const Services = () => {
  const [location, setLocation] = useState('');
  const [locationDetected, setLocationDetected] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(true);

  useEffect(() => {
    detectUserLocation();
  }, []);

  const detectUserLocation = async () => {
    setDetectingLocation(true);
    
    if (!navigator.geolocation) {
      setDetectingLocation(false);
      setLocation('international');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
          );
          const data = await response.json();
          
          const country = data.address?.country || data.address?.country_code;
          const isIndia = country?.toLowerCase() === 'india' || data.address?.country_code?.toLowerCase() === 'in';
          
          setLocation(isIndia ? 'india' : 'international');
          setLocationDetected(true);
          setDetectingLocation(false);
        } catch (error) {
          console.error('Error detecting location:', error);
          setLocation('international');
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        setLocation('international');
        setDetectingLocation(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  const services = [
    {
      title: 'Individual Therapy',
      priceINR: '₹3,000',
      priceUSD: '$60',
      duration: '60 min',
      description: 'One-on-one sessions tailored to your unique needs and goals. Perfect for working through personal challenges, building self-awareness, and developing coping strategies.',
      image: 'https://images.unsplash.com/photo-1610905277069-8cd4a85fbff2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
      icon: Heart,
      benefits: [
        'Personalized therapeutic approach',
        'Deep exploration of thoughts and emotions',
        'Develop healthy coping mechanisms',
        'Work at your own pace'
      ]
    },
    {
      title: 'Couples Therapy',
      priceINR: '₹4,000',
      priceUSD: '$80',
      duration: '75 min',
      description: 'Strengthen your relationship through improved communication, conflict resolution, and deeper understanding. Navigate challenges together and build a stronger foundation.',
      image: 'https://images.unsplash.com/photo-1591630060069-7cb1f16261b4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
      icon: Users,
      benefits: [
        'Improve communication patterns',
        'Resolve conflicts constructively',
        'Rebuild trust and intimacy',
        'Strengthen emotional connection'
      ]
    },
    {
      title: 'Adolescent Therapy',
      priceINR: '₹3,500',
      priceUSD: '$70',
      duration: '60 min',
      description: 'A dedicated space for adolescents between 12 and 18 years of age to process emotions, navigate peer relationships, academic stress, and identity questions.',
      image: 'https://images.pexels.com/photos/112361/pexels-photo-112361.jpeg?auto=compress&cs=tinysrgb&w=800',
      icon: Brain,
      benefits: [
        'Emotionally safe and age-appropriate space',
        'Support through academic and social pressures',
        'Identity, self-esteem, and peer relationship work',
        'Optional parental check-ins handled with care'
      ]
    },
    {
      title: 'Supervision',
      priceINR: '₹2,500',
      priceUSD: '₹2,500',
      duration: '60 min',
      fixedPrice: true,
      description: 'A reflective space for therapists to explore complex cases, countertransference, ethical dilemmas, and their own growth as practitioners. I bring my experience across work with adolescents, adults, and couples to every supervision session.',
      image: 'https://images.unsplash.com/photo-1572544405078-45963c80d026?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxub3RlYm9vayUyMHBlbiUyMGRlc2slMjB3YXJtJTIwbGlnaHQlMjBtaW5pbWFsJTIwY2FsbSUyMHdyaXRpbmd8ZW58MHx8fHwxNzg2MDc2Mjc4fDA&ixlib=rb-4.1.0&q=85',
      icon: Briefcase,
      benefits: [
        'Case consultation and clinical guidance',
        'Countertransference and self-of-therapist work',
        'Ethical dilemmas and boundary navigation',
        'Confidence building for early-career therapists'
      ]
    },
  ];

  const focusAreas = [
    { title: 'Relationship Challenges', description: 'Navigate complex dynamics in romantic, family, or professional relationships' },
    { title: 'Anxiety & Stress', description: 'Develop tools to manage overwhelming emotions and find calm' },
    { title: 'Complex PTSD', description: 'Heal from prolonged trauma with compassionate, trauma-informed care' },
    { title: 'Childhood Trauma & Neglect', description: 'Process and heal from early life experiences that still impact you' },
    { title: 'Depression', description: 'Work through feelings of hopelessness and rediscover joy' },
    { title: 'Body Image & Eating Disorders', description: 'Build a healthier relationship with your body and food' },
    { title: 'Marriage Counselling', description: 'Strengthen your partnership and work through marital challenges' },
    { title: 'Adults with ADHD', description: 'Develop strategies for focus, organization, and emotional regulation' },
    { title: 'Grief & Loss', description: 'Process loss and find meaning while honoring your emotions' },
    { title: 'Gender & Sexuality', description: 'Explore identity in a safe, affirming, non-judgmental space' },
    { title: 'Major Life Transitions', description: 'Navigate significant changes with support and clarity' }
  ];

  const getPrice = (service) => {
    if (service.fixedPrice) return service.priceINR;
    return location === 'india' ? service.priceINR : service.priceUSD;
  };

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-14 px-6 text-center">
        <div className="max-w-4xl mx-auto space-y-5">
          <h1 className="text-4xl md:text-5xl font-heading font-light tracking-tight text-foreground" data-testid="services-heading">
            Therapy <span className="italic text-primary">Services</span>
          </h1>
          <p className="text-lg text-muted-foreground">
            Compassionate, evidence-based therapy tailored to your unique journey
          </p>
        </div>
      </section>

      {/* Location Indicator with Hidden Toggle */}
      <section className="px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-end">
            <details className="group relative">
              <summary className="flex items-center gap-2 cursor-pointer list-none text-sm text-muted-foreground hover:text-foreground transition-colors">
                <MapPin size={14} />
                {detectingLocation ? (
                  <span className="animate-pulse">Detecting location...</span>
                ) : (
                  <span>
                    Showing {location === 'india' ? 'INR' : 'USD'} prices
                  </span>
                )}
                <ChevronDown size={14} className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border p-4 z-20 w-64">
                <p className="text-xs text-muted-foreground mb-3">
                  Update your location to see the appropriate pricing:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setLocation('india'); setLocationDetected(true); }}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      location === 'india' 
                        ? 'border-primary bg-primary/5 text-primary font-medium' 
                        : 'border-muted text-muted-foreground hover:border-primary/30'
                    }`}
                    data-testid="location-toggle-india"
                  >
                    India (INR)
                  </button>
                  <button
                    onClick={() => { setLocation('international'); setLocationDetected(true); }}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      location === 'international' 
                        ? 'border-primary bg-primary/5 text-primary font-medium' 
                        : 'border-muted text-muted-foreground hover:border-primary/30'
                    }`}
                    data-testid="location-toggle-international"
                  >
                    International (USD)
                  </button>
                </div>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Services - Medium Cards */}
      <section className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  className="overflow-hidden rounded-2xl border shadow-sm hover:shadow-xl transition-all bg-white group"
                  data-testid={`service-detail-${service.title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-2">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                        <Icon size={20} className="text-white" />
                      </div>
                    </div>
                    {/* Duration Badge */}
                    <div className="absolute top-4 right-4 flex items-center gap-1 text-xs text-white bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      <Clock size={12} />
                      {service.duration}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-2xl font-heading font-medium text-foreground mb-3">
                      {service.title}
                    </h2>
                    
                    {/* Price */}
                    <div className="mb-4">
                      <span className="text-3xl font-heading font-bold text-accent">
                        {detectingLocation ? '...' : getPrice(service)}
                      </span>
                      <span className="text-sm text-muted-foreground ml-2">per session</span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Benefits */}
                    <div className="space-y-2 mb-6">
                      {service.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-start text-sm">
                          <span className="text-primary mr-2 mt-0.5">✓</span>
                          <span className="text-muted-foreground">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* Book Button */}
                    <Link to="/book">
                      <Button 
                        className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6" 
                        data-testid={`book-${service.title.toLowerCase().replace(' ', '-')}`}
                      >
                        Book Now <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 px-6 bg-secondary/30" data-testid="focus-areas-detail">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-foreground mb-4">
              Areas of Focus
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              I work with clients on a wide range of issues, bringing expertise and compassion to each unique situation
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {focusAreas.map((area) => (
              <Card
                key={area.title}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-5 hover:shadow-md transition-all"
                data-testid={`focus-area-detail-${area.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <h3 className="text-lg font-heading font-medium text-foreground mb-2">{area.title}</h3>
                <p className="text-muted-foreground text-sm">{area.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
